import { useAppInstallPrompt } from '@src/providers/AppInstallPromptProvider';
import { envConfig } from '@src/utils/envConfig';
import { useDisplayMode } from '@src/utils/getDisplayMode';
import {
  Box,
  Button,
  ButtonVariants,
  Flex,
  FormControlSizes,
  HStack,
  Icon,
  IconButton,
  IconName,
  Text,
  useMobileModal,
} from '@talos/kyoko';
import { useCallback, useEffect, useMemo, useState } from 'react';
import styled, { useTheme } from 'styled-components';

export const MOBILE_PWA_INSTALL_BANNER_HEIGHT = 60;

const MOBILE_PWA_INSTALL_BANNER_DISMISSED_KEY = 'ava-mobile-pwa-install-banner-dismissed';
const INSTALLABLE_ICON_ENVS = ['alpha', 'local', 'prod', 'sandbox', 'staging', 'uat'] as const;

interface MobilePwaInstallBannerProps {
  onVisibilityChange: (isVisible: boolean) => void;
}

export function MobilePwaInstallBanner({ onVisibilityChange }: MobilePwaInstallBannerProps) {
  const { installPrompt, promptInstall } = useAppInstallPrompt();
  const displayMode = useDisplayMode() ?? 'browser';
  const isIos = useMemo(() => isIosDevice(), []);
  const [isDismissed, setIsDismissed] = useState(() => readDismissedState());
  const isInstalledApp = displayMode === 'standalone' || displayMode === 'minimal-ui';
  const isVisible = !isInstalledApp && !isDismissed && (isIos || installPrompt != null);
  const { openModal } = useMobileModal();
  const theme = useTheme();
  const bannerBackground = theme.colors.blue.dim;

  useEffect(() => {
    onVisibilityChange(isVisible);
  }, [isVisible, onVisibilityChange]);

  // Update the theme color to the banner background color when the banner is visible
  useEffect(() => {
    if (!isVisible) {
      return;
    }

    const meta = document.querySelector('meta[name="theme-color"]');
    const previousThemeColor = meta?.getAttribute('content');

    meta?.setAttribute('content', bannerBackground);

    return () => {
      if (previousThemeColor != null) {
        meta?.setAttribute('content', previousThemeColor);
      }
    };
  }, [bannerBackground, isVisible]);

  const handleDismiss = useCallback(() => {
    window.localStorage.setItem(MOBILE_PWA_INSTALL_BANNER_DISMISSED_KEY, 'true');
    setIsDismissed(true);
  }, []);

  const handleInstall = useCallback(async () => {
    const outcome = await promptInstall();
    if (outcome === 'accepted') {
      handleDismiss();
    }
  }, [handleDismiss, promptInstall]);

  const showIosInstructions = useCallback(() => {
    openModal({
      title: 'Install Talos',
      useSheetContent: true,
      content: (
        <Box fontSize="fontSizeLarge" lineHeight={1.5} color="colorTextImportant">
          <HStack my="spacing32">
            <AppIcon alt="" src={getAppIconSrc()} size={64} />
          </HStack>
          <p>Use Talos like an app for faster access and a full-screen mobile experience.</p>
          <InstructionList>
            <li>
              Tap <Icon icon={IconName.ShareIos} size={14} /> Share
            </li>
            <li>
              Tap <Icon icon={IconName.ChevronDown} size={14} /> Show more
            </li>
            <li>
              Tap <Icon icon={IconName.PlusBox} size={14} /> Add to Home Screen.
            </li>
          </InstructionList>
        </Box>
      ),
    });
  }, [openModal]);

  if (!isVisible) {
    return null;
  }

  return (
    <BannerWrapper data-testid="mobile-pwa-install-banner">
      <AppIcon alt="" src={getAppIconSrc()} />
      <Flex flex={1} minWidth={0} flexDirection="column" gap="spacing2">
        <Text color="colorTextImportant" weight="fontWeightMedium">
          Install Talos
        </Text>
        <Text color="colorTextImportant" opacity={0.8} size="fontSizeXs">
          Faster access and a full-screen mobile experience.
        </Text>
      </Flex>
      <HStack gap="spacing2">
        {isIos ? (
          <IconButton
            round
            icon={IconName.QuestionMarkCircle}
            size={FormControlSizes.Small}
            variant={ButtonVariants.Default}
            aria-label="Show install instructions"
            onClick={showIosInstructions}
            data-testid="mobile-pwa-install-button"
          />
        ) : (
          <Button
            size={FormControlSizes.Small}
            variant={ButtonVariants.Default}
            onClick={handleInstall}
            data-testid="mobile-pwa-install-button"
          >
            Install
          </Button>
        )}
        <IconButton
          ghost
          round
          icon={IconName.Close}
          size={FormControlSizes.Small}
          aria-label="Dismiss install banner"
          onClick={handleDismiss}
          data-testid="mobile-pwa-install-dismiss-button"
        />
      </HStack>
    </BannerWrapper>
  );
}

function readDismissedState() {
  return window.localStorage.getItem(MOBILE_PWA_INSTALL_BANNER_DISMISSED_KEY) === 'true';
}

function isIosDevice() {
  const { maxTouchPoints, userAgent } = window.navigator;
  return /iPad|iPhone|iPod/.test(userAgent) || (userAgent.includes('Mac') && maxTouchPoints > 1);
}

function getAppIconSrc() {
  const iconEnv = INSTALLABLE_ICON_ENVS.includes(envConfig.VITE_AVA_ENV as (typeof INSTALLABLE_ICON_ENVS)[number])
    ? envConfig.VITE_AVA_ENV
    : 'local';

  return `/assets/icons/${iconEnv}-180.png`;
}

const BannerWrapper = styled(Box)`
  align-items: center;
  background: ${({ theme }) => theme.colors.blue.dim};
  box-sizing: border-box;
  display: flex;
  gap: ${({ theme }) => theme.spacing8}px;
  height: ${MOBILE_PWA_INSTALL_BANNER_HEIGHT}px;
  left: 0;
  padding: ${({ theme }) => theme.spacing12}px;
  padding-top: max(env(safe-area-inset-top), 12px);
  position: fixed;
  right: 0;
  top: 0;
  z-index: 1001;
`;

const AppIcon = styled.img<{ size?: number }>`
  border-radius: ${({ theme }) => theme.borderRadiusLarge}px;
  flex-shrink: 0;
  height: ${({ size = 36 }) => size}px;
  width: ${({ size = 36 }) => size}px;
`;

const InstructionList = styled.ol`
  list-style: decimal;
  padding-left: ${({ theme }) => theme.spacing24}px;
  margin-top: ${({ theme }) => theme.spacing24}px;
  color: ${({ theme }) => theme.colorTextImportant};

  li {
    margin-bottom: ${({ theme }) => theme.spacing12}px;

    &::marker {
      color: ${({ theme }) => theme.colorTextDefault};
    }
  }
`;
